import { NotificationDTO } from '@/api/DTO/notification/notification.interface';
import { NotificationTargetType } from '@/api/enum/notification.enum';

/** Unread counts above this show as "99+" on the bell badge. */
export const NOTIFICATION_BADGE_MAX = 99;

/**
 * Unread row styling, shared by the popover and the summary table so a notification
 * looks identical in both places.
 *
 * Cyan rather than amber: every notification in this product is an alert, so
 * "alert-ness" is constant across every row and cannot be what a colour encodes.
 * The only attribute that varies row to row is whether it has been opened.
 *
 * Cyan rather than the badge's red, too. The badge and the bar do different jobs at
 * different scales: the badge is a momentary count asking for attention at app
 * level, where red reads as "notifications" and nobody mistakes it for an error. A
 * 3px bar repeated down a list of rows is a persistent state, and at that scale red
 * starts reading as "these rows are broken". Cyan carries "new" without the alarm —
 * the same split Outlook uses, with its blue unread bar under a red badge.
 */
export const UNREAD_BACKGROUND = '#E8F7FB';
export const UNREAD_BACKGROUND_HOVER = '#D8F0F6';
/** Mirrors `palette.primary.main`; inlined because the bar is built at module scope. */
export const UNREAD_ACCENT = '#1BBED7';
/**
 * Left edge bar marking an unread row. An inset shadow rather than a border, so it
 * never takes a pixel off the row and shifts every cell along.
 */
export const UNREAD_ACCENT_BAR = `inset 3px 0 0 ${UNREAD_ACCENT}`;

/**
 * How many of the newest notifications the popover previews. The popover is a
 * shortcut, not a second list — anything past this is reached through "View all".
 */
export const NOTIFICATION_PREVIEW_SIZE = 10;

export const NOTIFICATION_LIST_PATH = '/notifications';

export function notificationDetailPath(id: string) {
	return `${NOTIFICATION_LIST_PATH}/detail/${id}`;
}

/**
 * The record a notification is about, or `undefined` when there is nothing to open
 * — the annual Face Sheet reminder, which is informational only, and every
 * notification until the backend starts sending `targetType`.
 *
 * `dataId` alone cannot answer this: it is the same `user` record from
 * pre-admission through to member, so which page it belongs on depends on where
 * that record has got to, which is why the backend resolves it at send time.
 * Guessing from `notificationType` would send staff to the wrong section.
 */
export function resolveNotificationRecordPath(
	notification: NotificationDTO
): string | undefined {
	const { dataId, targetType } = notification;
	if (!dataId || targetType === undefined) return undefined;

	switch (targetType) {
		case NotificationTargetType.preAdmission:
			return `/pre-admission/pre-admission-list/detail/${dataId}`;
		case NotificationTargetType.admissionRequest:
			return `/member-management/admission-request-list/detail/${dataId}`;
		case NotificationTargetType.member:
			return `/member-management/member-list/detail/${dataId}`;
		case NotificationTargetType.intervention:
			return `/member-management/member-list/detail/${dataId}?tab=intervention`;
		default:
			return undefined;
	}
}

/**
 * i18n key for the button that opens the related record, so the label names the
 * destination ("View pre-admission") instead of a vague "View record".
 */
export function notificationRecordLabelKey(
	notification: NotificationDTO
): string | undefined {
	switch (notification.targetType) {
		case NotificationTargetType.preAdmission:
			return 'notification.viewPreAdmission';
		case NotificationTargetType.admissionRequest:
			return 'notification.viewAdmissionRequest';
		case NotificationTargetType.member:
			return 'notification.viewMember';
		case NotificationTargetType.intervention:
			return 'notification.viewIntervention';
		default:
			return undefined;
	}
}
